import React from "react";
import { ChangeEvent } from "react";
import ReactModal from "react-modal";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import NoteBox from "../NoteBox";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import ViewToolbar from "../ViewToolbar";

import { apiGet, apiPut, apiDelete, apiPost } from "../../Api";
import { formatDateLocal, formatName, formatTelephone, formatDateTimeLocal, formatUSD } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../form.css";
import "../../icomoon.css";
import "../viewer.css";

interface IAppointmentInsuranceViewState {
    isLoading: boolean;
    hasNoteChanged: boolean;
    loadError?: string;

    appointment?: any;

    hideInsuranceNoteControls: boolean;
    hideNoteControls: boolean;
    allowNoteUploads: boolean;

    isBillingModalOpen: boolean;
    billingNote: string;
    billingBilledAmount: string;
    billingPaidAmount: string;
    billingBilledDeductible: string;
    billingBilledCoinsurance: string;
    billingModalLoading: boolean | string;
    billingModalType: "clinic" | "surgical",

    employees: any[];
}

class AppointmentInsuranceView extends React.Component<RouteComponentProps<any> & IAuthProps, IAppointmentInsuranceViewState> {
    constructor(props: Readonly<RouteComponentProps<any> & IAuthProps>) {
        super(props);

        const state: IAppointmentInsuranceViewState = {
            isLoading: true,
            hasNoteChanged: false,

            hideInsuranceNoteControls: !props.authContext.hasRight("appt:note:ins"),
            hideNoteControls: !this.props.authContext.hasRight("appt:write"),
            allowNoteUploads: props.authContext.hasRight("appt:note:upload"),

            isBillingModalOpen: false,

            employees: [],
            billingNote: "",
            billingBilledAmount: "",
            billingPaidAmount: "",
            billingBilledDeductible: "",
            billingBilledCoinsurance: "",
            billingModalLoading: false,
            billingModalType: "clinic",
        };

        this.state = state;

        this.handleInsuranceNote = this.handleInsuranceNote.bind(this);
        this.handleInsuranceNoteDelete = this.handleInsuranceNoteDelete.bind(this);

        this.dialogInputChanged = this.dialogInputChanged.bind(this);

        this.showBillingDialog = this.showBillingDialog.bind(this);
        this.closeBillingModal = this.closeBillingModal.bind(this);

        this.billingFullyPaid = this.billingFullyPaid.bind(this);
        this.billingPartiallyPaid = this.billingPartiallyPaid.bind(this);
        this.billingNeverPaid = this.billingNeverPaid.bind(this);
        this.billingBilledOut = this.billingBilledOut.bind(this);
    }

    public async componentDidMount() {
        try {
            this.loadAppointment();
        }
        catch (e) {
            this.setState({
                loadError: e.message
            });
        }
    }

    public render() {
        if (this.state.loadError) {
            return <div className="viewer-root">
                <div className="error-box">
                    Error loading surgery appointment:<br />
                    <br />
                    {this.state.loadError}
                </div>
            </div>
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-full-root lds-dual-ring-big" />;
        }

        const { billing } = this.state.appointment;

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="right:placeholder">
                            <button type="button" onClick={() => { }}><i className="icon-gears" /><span>Placeholder button</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ReactModal
                    isOpen={this.state.isBillingModalOpen} onRequestClose={this.closeBillingModal}
                    overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-billing-portal"
                    bodyOpenClassName="modal-billing-open"
                    contentLabel="Update Billing Information">
                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Update {this.state.billingModalType === "clinic" ? "WCAS" : "Read Fees"} Billing Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-billed" className={"required"}>Amount Billed</label>
                                    <input type="number" id="frm-billing-billed" name="billingBilledAmount" value={this.state.billingBilledAmount} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-paid" className={"required"}>Amount Paid</label>
                                    <input type="number" id="frm-billing-paid" name="billingPaidAmount" value={this.state.billingPaidAmount} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group col-50" />
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-billing-deductible" className={"required"}>Deductible</label>
                                    <input type="number" id="frm-billing-deductible" name="billingBilledDeductible" value={this.state.billingBilledDeductible} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                                <div className="input-form-group col-25">
                                    <label htmlFor="frm-billing-coinsurance" className={"required"}>Co-Insurance</label>
                                    <input type="number" id="frm-billing-coinsurance" name="billingBilledCoinsurance" value={this.state.billingBilledCoinsurance} onChange={this.dialogInputChanged} step={0.01} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-billing-note">Note <small>(optional)</small></label>
                                    <textarea id="frm-billing-note" name="billingNote" value={this.state.billingNote} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            {
                                this.state.billingModalLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.billingModalLoading) === "string"
                                        ? <span className="error-text">{this.state.billingModalLoading}</span>
                                        : ""
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <button type="button" className="button" onClick={this.billingBilledOut} disabled={!!this.state.billingPaidAmount}>Billed Out</button>
                                <button type="button" className="button red" onClick={this.billingNeverPaid} disabled={!!this.state.billingPaidAmount}>Never Paid</button>
                                <button type="button" className="button yellow" onClick={this.billingPartiallyPaid} disabled={!this.state.billingPaidAmount || !this.state.billingBilledDeductible}>Partially Paid</button>
                                <button type="button" className="button green" onClick={this.billingFullyPaid} disabled={!this.state.billingPaidAmount || !this.state.billingBilledDeductible}>Fully Paid</button>
                                <button type="button" className="button" onClick={this.closeBillingModal}>Cancel</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Patient Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>Name</dt>
                                    <dd>{formatName(this.state.appointment.patient.name)}</dd>
                                </div>
                                <div>
                                    <dt>Date of Birth</dt>
                                    <dd>{formatDateLocal(this.state.appointment.patient.birthDate)}</dd>
                                </div>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Phone Number</dt>
                                    {
                                        this.state.appointment.patient.phone
                                            ? <dd><a href={`tel:${this.state.appointment.patient.phone}`}>{formatTelephone(this.state.appointment.patient.phone)}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                                <div>
                                    <dt>Email Address</dt>
                                    {
                                        this.state.appointment.patient.email
                                            ? <dd><a href={`mailto:${this.state.appointment.patient.email}`} rel="noopener noreferrer" target="_blank">{this.state.appointment.patient.email}</a></dd>
                                            : <dd>N/A</dd>
                                    }
                                </div>
                            </div>

                            <div>
                                <dt>Sex</dt>
                                <dd>{this.sexToString(this.state.appointment.patient.sex)}</dd>
                            </div>

                            <div className="info-list-row">
                                <div>
                                    <dt>Insurance Provider</dt>
                                    <dd>{this.state.appointment.patient.insuranceProvider}</dd>
                                </div>
                                <div>
                                    <dt>Chart #</dt>
                                    <dd>{this.state.appointment.patient.chartNumber || "Unspecified"}</dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                    <section className="info-block">
                        <header>
                            <h3>Case Information</h3>
                        </header>

                        <dl className="info-list">
                            <dt>Location</dt>
                            <dd>{this.state.appointment.location.name}</dd>
                            <dd>
                                <small>
                                    {this.state.appointment.location.address.street} {`${this.state.appointment.location.address.city}, ${this.state.appointment.location.address.state} ${this.state.appointment.location.address.zip}`}
                                </small>
                            </dd>

                            <div className="info-list-row">
                                <div className="col-66">
                                    <dt>Date & Time</dt>
                                    <dd>{formatDateTimeLocal(this.state.appointment.appointmentTimestamp)}</dd>
                                </div>
                                <div className="col-33">
                                    <dt>Status</dt>
                                    <dd>{this.statusToText(this.state.appointment.status)}</dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div className="col-66">
                                    <dt>Appointment Type</dt>
                                    <dd>{this.state.appointment.appointmentType.name}</dd>
                                </div>
                                <div className="col-33">
                                    <dt>Appointment</dt>
                                    <dd><Link to={`/appointments/${this.props.match.params.id}`}>View full appointment</Link></dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Insurance Information</h3>
                        </header>
                        <dl className="info-list">
                            <div className="info-list-row">
                                <div>
                                    <dt>WCAS Billed</dt>
                                    <dd>{formatUSD(billing.wcasBilled)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Collected</dt>
                                    <dd>{formatUSD(billing.wcasCollected)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Deductible</dt>
                                    <dd>{formatUSD(billing.wcasDeductible)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Co-Insurance</dt>
                                    <dd>{formatUSD(billing.wcasCoinsurance)}</dd>
                                </div>
                                <div>
                                    <dt>WCAS Status</dt>
                                    <dd className={this.getColorClassName("cell", billing.wcasStatus)}>{billing ? this.billingStatusToString(billing.wcasStatus) : "-"}</dd>
                                </div>
                                <div className="col-smallest col-button">
                                    <dd>
                                        <button type="button" className="button small" onClick={() => this.showBillingDialog("clinic")}>Edit</button>
                                    </dd>
                                </div>
                            </div>
                            <div className="info-list-row">
                                <div>
                                    <dt>Read fees Billed</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesBilled)}</dd>
                                </div>
                                <div>
                                    <dt>Read fees Collected</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesCollected)}</dd>
                                </div>
                                <div>
                                    <dt>Read fees Deductible</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesDeductible)}</dd>
                                </div>
                                <div>
                                    <dt>Read fees Co-Insurance</dt>
                                    <dd>{formatUSD(billing.surgeonsFeesCoinsurance)}</dd>
                                </div>
                                <div>
                                    <dt>Read fees Status</dt>
                                    <dd className={this.getColorClassName("cell", billing.surgeonStatus)}>{billing ? this.billingStatusToString(billing.surgeonStatus) : "-"}</dd>
                                </div>
                                <div className="col-smallest col-button">
                                    <dd>
                                        <button type="button" className="button small" onClick={() => this.showBillingDialog("surgical")}>Edit</button>
                                    </dd>
                                </div>
                            </div>
                        </dl>
                    </section>
                </div>
                &nbsp;
                <div className="column-root">
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Notes</h3>
                            </header>
                            <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.appointment.notes} onNewNote={this.handleInsuranceNote} hideControls={this.state.hideNoteControls} />
                        </section>
                    </div>
                    <div className="row-root  row-root-auto">
                        <section className="info-block">
                            <header>
                                <h3>Insurance Notes</h3>
                            </header>

                            {!this.state.appointment.insuranceNotes || this.state.appointment.insuranceNotes.length === 0 ? "No notes" : ""}
                            <NoteBox notes={this.state.appointment.insuranceNotes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleInsuranceNote} hideControls={this.state.hideInsuranceNoteControls} allowDeletion={this.props.authContext.hasRight("appt:delete:note")} onNoteDelete={this.handleInsuranceNoteDelete}  />
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;


        if (e.currentTarget.type === "number" && !(e.currentTarget as any).step) {
            this.setState({
                [name]: parseInt(value, 10)
            } as any);
        }
        else {
            this.setState({
                [name]: value
            } as any);
        }
    }

    private async loadAppointment() {
        try {
            this.setState({
                isLoading: true,
            });

            const id = this.props.match.params.id;
            const appointment = await apiGet<any>(`appointments/${id}?mode=insurance`);

            if (appointment) {
                // Sort by time
                appointment.insuranceNotes && appointment.insuranceNotes.sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp));

                if (appointment.notes) {
                    for (const note of appointment.notes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (appointment.insuranceNotes) {
                    for (const note of appointment.insuranceNotes) {
                        note.author = formatName(note.employee.name);
                    }
                }

                if (!appointment.billing) {
                    appointment.billing = {};
                }
            }

            this.setState({
                isLoading: false,
                loadError: undefined,
                appointment,
            });
        }
        catch (e) {
            this.setState({
                isLoading: false,
                loadError: e.message
            });
        }
    }

    private sexToString(sex: string) {
        switch (sex) {
            case "f":
            case "F": return "Female";
            case "m":
            case "M": return "Male";
            case null:
            case undefined: return "Unspecified";
            default: return `Unknown (${sex})`;
        }
    }

    private statusToText(status: number) {
        switch (status) {
            case 0: return "Pending";
            case 1: return "Active";
            case 2: return "Canceled";
            case 3: return "No Show";
            case 4: return "Complete";
            case 5: return "Pending Reschdule";
            case 6: return "Pending Update";
            default: return `Unknown: ${status}`;
        }
    }

    private async handleInsuranceNote(body: string) {
        // Send note to server
        const note = await apiPost<any>(`appointments/${this.props.match.params.id}/notes/insurance`, { body });
        note.author = formatName(note.employee.name);

        this.setState(s => {
            s.appointment.insuranceNotes.push(note);

            return {
                hasNoteChanged: false,
                appointment: s.appointment,
            };
        });
    }

    private async handleInsuranceNoteDelete(index: number) {
        // No try-catch because errors are handled by NoteBox
        const note = this.state.appointment.insuranceNotes[index];
        const unixTime = new Date(note.timestamp).getTime();

        await apiDelete(`appointments/${this.state.appointment.id}/notes/insurance/${note.employeeId}/${unixTime}`);

        this.setState(s => {
            s.appointment.insuranceNotes.splice(index, 1);

            return {
                appointment: s.appointment
            }
        });
    }

    private showBillingDialog(kind: "clinic" | "surgical") {
        this.setState({
            billingModalType: kind,
            billingBilledAmount: (kind === "clinic" ? this.state.appointment.billing.wcasBilled : this.state.appointment.billing.surgeonsFeesBilled) || "",
            billingPaidAmount: (kind === "clinic" ? this.state.appointment.billing.wcasCollected : this.state.appointment.billing.surgeonsFeesCollected) || "",
            billingBilledDeductible: (kind === "clinic" ? this.state.appointment.billing.wcasDeductible : this.state.appointment.billing.surgeonsFeesDeductible) || "",
            billingBilledCoinsurance: (kind === "clinic" ? this.state.appointment.billing.wcasCoinsurance : this.state.appointment.billing.surgeonsFeesCoinsurance) || "",
            billingModalLoading: false,
            billingNote: "",

            isBillingModalOpen: true
        });
    }

    private closeBillingModal() {
        this.setState({
            isBillingModalOpen: false
        });
    }

    private async saveBillingInformation(kind: "clinic" | "surgical", status: "billed" | "never" | "partial" | "full") {
        try {
            this.setState({
                billingModalLoading: true,
            });

            const data = {
                status: this.statusToNumber(status),

                billed: this.state.billingBilledAmount,
                collected: this.state.billingPaidAmount,

                deductible: this.state.billingBilledDeductible,
                coinsurance: this.state.billingBilledCoinsurance,

                note: this.state.billingNote,
            };

            await apiPut(`appointments/${this.state.appointment.id}/billing/${kind}`, data);

            this.setState({
                billingModalLoading: false,
                isBillingModalOpen: false,
            }, () => this.loadAppointment());
        }
        catch (e) {
            this.setState({
                billingModalLoading: e.message || e || "Could not save billing info"
            });
        }
    }

    private billingBilledOut() {
        this.saveBillingInformation(this.state.billingModalType, "billed");
    }

    private billingNeverPaid() {
        this.saveBillingInformation(this.state.billingModalType, "never");
    }

    private billingPartiallyPaid() {
        this.saveBillingInformation(this.state.billingModalType, "partial");
    }

    private billingFullyPaid() {
        this.saveBillingInformation(this.state.billingModalType, "full");
    }

    private statusToNumber(status: "billed" | "never" | "partial" | "full") {
        switch (status) {
            case "full": return 3;
            case "partial": return 2;
            case "never": return 1;
            case "billed": return 4;
            default: return 0;
        }
    }

    private billingStatusToString(status?: number) {
        switch (status) {
            case 1: return "Never Paid";
            case 2: return "Partially Paid";
            case 3: return "Fully Paid";
            case 4: return "Billed Out";
            case 0:
            case undefined: return "Not Specified";
            default: return `Unknown (${status})`;
        }
    }

    private getColorClassName(kind: "row" | "cell", status?: number) {
        if (!status)
            return "";

        if (status === 1) {
            return `${kind}-red`;
        }
        if (status === 3) {
            return `${kind}-green`;
        }
        if (status >= 4) {
            return "";
        }
        if (status >= 2) {
            return `${kind}-yellow`;
        }

        return "";
    }
}

export default withTitle("Appointment Insurance Details", withAuth(AppointmentInsuranceView));
